/**
 * Makes the round icons drift toward (or away from) the pointer as it gets close.
 * Works with a mouse only, stays off for reduced motion, and stops when the section is off screen.
 */
class AvalonDeviceFeatures extends HTMLElement {
  #icons = [];
  #pointer = null;
  #frame = 0;
  #listening = false;
  #observer = null;
  #radius = 280;
  #distance = 44;
  #direction = 1;
  #motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  #fineQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

  connectedCallback() {
    if (this.dataset.enabled !== 'true') return;

    this.#radius = Number(this.dataset.radius) || this.#radius;
    this.#distance = Number(this.dataset.distance) || this.#distance;
    this.#direction = this.dataset.mode === 'away' ? -1 : 1;
    this.#icons = Array.from(this.querySelectorAll('[data-avalon-icon]')).map((el) => ({
      el,
      x: 0,
      y: 0,
      scale: 1,
      force: 0,
    }));

    this.#observer = new IntersectionObserver(this.#onIntersect);
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#stop();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.#icons.forEach(({ el }) => this.#reset(el));
  }

  get #active() {
    return !this.#motionQuery.matches && this.#fineQuery.matches;
  }

  #onIntersect = ([entry]) => {
    if (entry.isIntersecting && this.#active) {
      this.#start();
    } else {
      this.#stop();
    }
  };

  #start() {
    if (this.#listening) return;

    window.addEventListener('pointermove', this.#onMove, { passive: true });
    document.documentElement.addEventListener('pointerleave', this.#onLeave);
    this.#listening = true;
  }

  #stop() {
    if (!this.#listening) return;

    window.removeEventListener('pointermove', this.#onMove);
    document.documentElement.removeEventListener('pointerleave', this.#onLeave);
    this.#listening = false;
    this.#pointer = null;
    this.#schedule();
  }

  #onMove = (event) => {
    if (event.pointerType && event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;

    this.#pointer = { x: event.clientX, y: event.clientY };
    this.#schedule();
  };

  #onLeave = () => {
    this.#pointer = null;
    this.#schedule();
  };

  #schedule() {
    if (this.#frame) return;

    this.#frame = requestAnimationFrame(this.#tick);
  }

  #reset(el) {
    el.style.transform = '';
    el.style.removeProperty('--avalon-f');
  }

  #tick = () => {
    this.#frame = 0;
    let moving = false;

    this.#icons.forEach((icon) => {
      const { el } = icon;
      const rect = el.getBoundingClientRect();
      // Centre without the current offset, so the target never feeds back into itself
      const cx = rect.left + rect.width / 2 - icon.x;
      const cy = rect.top + rect.height / 2 - icon.y;

      let targetX = 0;
      let targetY = 0;
      let targetForce = 0;

      if (this.#pointer) {
        const dx = this.#pointer.x - cx;
        const dy = this.#pointer.y - cy;
        const distance = Math.hypot(dx, dy);

        if (distance < this.#radius && distance > 0) {
          const closeness = 1 - distance / this.#radius;
          const force = closeness * closeness;

          targetForce = force;
          targetX = (dx / distance) * force * this.#distance * this.#direction;
          targetY = (dy / distance) * force * this.#distance * this.#direction;
        }
      }

      icon.x += (targetX - icon.x) * 0.14;
      icon.y += (targetY - icon.y) * 0.14;
      icon.force += (targetForce - icon.force) * 0.14;

      const settled =
        Math.abs(targetX - icon.x) < 0.05 && Math.abs(targetY - icon.y) < 0.05 && Math.abs(targetForce - icon.force) < 0.005;

      if (settled && targetForce === 0) {
        icon.x = 0;
        icon.y = 0;
        icon.force = 0;
        this.#reset(el);
        return;
      }

      moving = true;
      el.style.transform = `translate3d(${icon.x.toFixed(2)}px, ${icon.y.toFixed(2)}px, 0) scale(${(1 + icon.force * 0.14).toFixed(3)})`;
      el.style.setProperty('--avalon-f', icon.force.toFixed(3));
    });

    if (moving || this.#pointer) this.#schedule();
  };
}

if (!customElements.get('avalon-device-features')) {
  customElements.define('avalon-device-features', AvalonDeviceFeatures);
}
